import React, { useEffect, useState } from "react";

import {
    Button,
    LabeledField,
    LanguageSegmentedControl,
    Modal,
    Stack,
    TextInput,
    ToggleSwitch,
} from "shared/ui";
import { CurrencyFormatter } from "shared/lib/currencyFormatter";
import { hasLocalizedValue, trimLocalizedMap } from "shared/lib/localizedMap";
import { createMoneyInputHandler } from "shared/lib/decimalInput";

import { ExternalIdField } from "features/ModifierSets/components/ExternalIdField";
import { ExternalOptionItem, ModifierDraft } from "features/ModifierSets/types";

import { Hint, LangRow } from "./styles";

const NO_SET_TYPE_HINT =
    "Укажите тип набора во внешнем меню — тогда вариант можно будет привязать";

interface ModifierDialogProps {
    open: boolean;
    modifier: ModifierDraft | null;
    locale: string;
    availableLanguages: string[];
    currencyFormatter: CurrencyFormatter;
    // Поле привязки показываем только заведениям с внешним меню, а редактировать
    // его можно, лишь когда у набора выбран тип iiko.
    showExternalBinding: boolean;
    externalBindingEnabled: boolean;
    externalOptions: ExternalOptionItem[];
    externalOptionsLoading: boolean;
    onClose: () => void;
    onSubmit: (modifier: ModifierDraft) => void;
}

export const ModifierDialog = ({
    open,
    modifier,
    locale,
    availableLanguages,
    currencyFormatter,
    showExternalBinding,
    externalBindingEnabled,
    externalOptions,
    externalOptionsLoading,
    onClose,
    onSubmit,
}: ModifierDialogProps) => {
    const [activeLang, setActiveLang] = useState<string>(locale);
    const [name, setName] = useState<Record<string, string>>({});
    const [priceToShow, setPriceToShow] = useState<string>("");
    const [price, setPrice] = useState<number>(0);
    const [isInStopList, setIsInStopList] = useState<boolean>(false);
    const [externalId, setExternalId] = useState<string>("");

    useEffect(() => {
        if (!open) return;

        setActiveLang(locale);
        setName(modifier ? { ...modifier.name } : {});
        setPrice(modifier ? modifier.sellPrice : 0);
        setPriceToShow(currencyFormatter.toShow(modifier ? modifier.sellPrice : 0, true));
        setIsInStopList(modifier ? modifier.isInStopList : false);
        setExternalId(modifier?.externalId || "");
    }, [open, modifier, locale, currencyFormatter]);

    const setPriceWrapper = createMoneyInputHandler(
        currencyFormatter.toSend, setPrice, setPriceToShow
    );

    const handleSubmit = () => {
        if (!hasLocalizedValue(name)) return;

        onSubmit({
            id: modifier ? modifier.id : null,
            name: trimLocalizedMap(name),
            sellPrice: price,
            isInStopList,
            externalId: externalId.trim() || null,
        });
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={modifier ? "Редактирование варианта" : "Новый вариант"}
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!hasLocalizedValue(name)}
                        onClick={handleSubmit}
                    >
                        Готово
                    </Button>
                </>
            }
        >
            <Stack>
                {availableLanguages.length > 1 && (
                    <LangRow>
                        <LanguageSegmentedControl
                            languages={availableLanguages}
                            activeLang={activeLang}
                            primaryLang={locale}
                            onChange={setActiveLang}
                            isMissing={(lang) => !name[lang]}
                        />
                    </LangRow>
                )}

                <LabeledField label="Название">
                    <TextInput
                        value={name[activeLang]}
                        onChange={(value) => setName({ ...name, [activeLang]: value })}
                        placeholder="Например, «Двойной эспрессо»"
                    />
                </LabeledField>

                <LabeledField
                    label="Доплата"
                    hint="Прибавляется к цене позиции, когда гость выбирает этот вариант"
                >
                    <TextInput value={priceToShow} onChange={setPriceWrapper} placeholder="0" />
                </LabeledField>

                <ToggleSwitch
                    checked={isInStopList}
                    onChange={setIsInStopList}
                    label="В стоп-листе"
                    hint="Вариант остаётся в наборе, но гость не может его выбрать"
                />

                {showExternalBinding && (
                    <LabeledField label="Привязка к внешнему меню">
                        {externalBindingEnabled ? (
                            <ExternalIdField
                                value={externalId}
                                onChange={setExternalId}
                                options={externalOptions}
                                loading={externalOptionsLoading}
                                locale={locale}
                                currencyFormatter={currencyFormatter}
                            />
                        ) : (
                            <Hint>{NO_SET_TYPE_HINT}</Hint>
                        )}
                    </LabeledField>
                )}
            </Stack>
        </Modal>
    );
};
